/**
 * kp-cart-drawer — two things Dawn's drawer leaves to the theme.
 *
 * It does not replace or patch cart-drawer.js. Dawn still owns opening,
 * closing, focus trapping and section rendering; this watches the `active`
 * class Dawn toggles and reacts to it. Nothing here is on the path between a
 * shopper and their cart — if this file fails to load, the drawer still works
 * exactly as Dawn shipped it.
 *
 * 1. SCROLL LOCK. Dawn locks the page with `overflow-hidden` on <body>. iOS
 *    Safari lets touch scrolling through that anyway, rubber-banding drags the
 *    page behind the drawer, and focusing the discount field scrolls the whole
 *    layout to lift the input into view. The reliable lock is to take the body
 *    out of flow at its current offset and put the offset back on close —
 *    without the restore, closing the drawer returns the shopper to the top of
 *    a page they were halfway down.
 *
 *    Dawn's class is left exactly as it was: cart-disclosure-modal.js reads
 *    `body.classList.contains('overflow-hidden')` to decide whether to restore
 *    the lock when it closes, and removing the class would strand that guard.
 *
 * 2. BACK DISMISSES THE DRAWER. Most of this store's traffic arrives from an
 *    ad, so the cart drawer is often the first overlay a shopper meets, on a
 *    phone, in an in-app browser. The back gesture is what people reach for to
 *    close an overlay — and with no history entry to go back to it takes them
 *    off the site instead, mid-purchase. Pushing one entry on open makes back
 *    mean "close the drawer" once, which is the only thing it should mean.
 */

class KpCartDrawer {
  constructor(drawer) {
    this.drawer = drawer;
    this.scrollY = 0;
    this.pushed = false;
    this.wasActive = drawer.classList.contains('active');

    new MutationObserver(() => this.sync()).observe(drawer, {
      attributes: true,
      attributeFilter: ['class'],
    });

    window.addEventListener('popstate', () => {
      // The entry is already gone, so there is nothing to unwind — just close.
      this.pushed = false;
      if (this.drawer.classList.contains('active')) this.drawer.close();
    });
  }

  sync() {
    const active = this.drawer.classList.contains('active');
    if (active === this.wasActive) return;
    this.wasActive = active;

    if (active) {
      this.lock();
      this.push();
    } else {
      this.unlock();
      this.pop();
    }
  }

  lock() {
    this.scrollY = window.scrollY;
    const { style } = document.body;
    style.position = 'fixed';
    style.top = `${-this.scrollY}px`;
    style.left = '0';
    style.right = '0';
    style.width = '100%';
  }

  unlock() {
    const { style } = document.body;
    if (!style.position) return;
    style.position = '';
    style.top = '';
    style.left = '';
    style.right = '';
    style.width = '';
    // Instant, not smooth: this is putting the page back where it was, not
    // taking the shopper somewhere.
    window.scrollTo(0, this.scrollY);
  }

  push() {
    if (this.pushed) return;
    history.pushState({ kpCartDrawer: true }, '');
    this.pushed = true;
  }

  /** Closed by the × , the scrim or Escape — so spend the entry we pushed. */
  pop() {
    if (!this.pushed) return;
    this.pushed = false;
    if (history.state?.kpCartDrawer) history.back();
  }
}

document.addEventListener('DOMContentLoaded', () => {
  const drawer = document.querySelector('cart-drawer');
  if (drawer) new KpCartDrawer(drawer);
});
